import React, { useState, useRef, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { useVarianceExplorer, useVePostings, useVeCostCenters } from "../hooks/useDashboardData";

interface Props {
  filters: { fiscalYear?: string; budgetVersion?: string; accountCategory?: string; glAccount?: string; costCenter?: string };
  onBack: () => void;
  initialQuarter?: string;
  onGlAccountSelect?: (glAccount: string) => void;
}

const BLUE = "#2590F4";
const ACTUAL_C = "#3F3CDD";
const GREEN = "#2EB85C";
const RED = "#DD3C57";

const CHART_PALETTE = [
  "#4C8EF7", "#F7634C", "#2EB85C", "#F5A623",
  "#9B59B6", "#1ABC9C", "#E74C3C", "#3498DB",
  "#E67E22", "#27AE60", "#8E44AD", "#16A085",
  "#C0392B", "#2980B9", "#D35400", "#1ABC9C",
  "#7F8C8D", "#F39C12", "#6C5CE7", "#00B894",
];

const QUARTER_OPTIONS = ["All", "Q1", "Q2", "Q3", "Q4"];

function indexColor(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length];
}

const VarianceExplorer: React.FC<Props> = ({ filters, onBack, initialQuarter, onGlAccountSelect }) => {
  const { data, isLoading, isFetching, error } = useVarianceExplorer(filters);
  const [quarterFilter, setQuarterFilter] = useState(initialQuarter ?? "All");
  const [quarterOpen, setQuarterOpen] = useState(false);
  const quarterRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"quarter" | "postings" | "costcenter">("quarter");
  // focusAccount is set by clicking table rows or chart bars — no dropdown
  const [focusAccount, setFocusAccount] = useState<string>("All");

  // Pass costCenter from filters to drill-down hooks so data aligns with sidebar selection
  const { data: postingsData, isLoading: postingsLoading } = useVePostings(
    filters.fiscalYear,
    focusAccount !== "All" ? focusAccount : undefined,
    filters.costCenter,
  );
  const { data: costCentersData, isLoading: ccLoading } = useVeCostCenters(
    filters.fiscalYear,
    focusAccount !== "All" ? focusAccount : undefined,
    filters.costCenter,
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (quarterRef.current && !quarterRef.current.contains(e.target as Node)) {
        setQuarterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset focusAccount when filters change so stale selection doesn't persist
  useEffect(() => {
    setFocusAccount("All");
  }, [filters.costCenter, filters.glAccount, filters.accountCategory, filters.fiscalYear]);

  if (isLoading) {
    return (
      <div className="ve-shell">
        <div className="ve-breadcrumb">
          <button className="ve-back-btn" onClick={onBack}>← Dashboard</button>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
          Loading variance data from Snowflake…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="ve-shell">
        <div className="ve-breadcrumb">
          <button className="ve-back-btn" onClick={onBack}>← Dashboard</button>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: RED, fontSize: 13 }}>
          {(error as any)?.message ?? "No data available."}
        </div>
      </div>
    );
  }

  const { overrunByQuarter, glVariance, topVariance, table, totalBudget, totalVariance } = data;

  return (
    <div className="ve-shell">

      {/* ── Header bar ──────────────────────────────────────────────────────── */}
      <div className="ve-header-bar">
        <div className="ve-header-titles">
          <h2 className="ve-page-title">Variance Explorer – FY{filters.fiscalYear ?? "2024"}</h2>
          <p className="ve-page-subtitle">Transaction Performance Management</p>
        </div>
      </div>

      {/* ── Breadcrumb bar ──────────────────────────────────────────────────── */}
      <div className="ve-breadcrumb">
        <button className="ve-back-btn" onClick={onBack}>← Dashboard</button>
        <span className="ve-breadcrumb-sep">/</span>
        <span className="ve-breadcrumb-path">{filters.accountCategory ?? "All Categories"}</span>
        <span className="ve-breadcrumb-sep">›</span>
        <span className="ve-breadcrumb-path">All Accounts</span>
        <span className="ve-breadcrumb-sep">›</span>
        <span className="ve-breadcrumb-current">GL Detail</span>
        {filters.costCenter && filters.costCenter !== "All" && (
          <span className="ve-chip" style={{ marginLeft: 8 }}>{filters.costCenter}</span>
        )}
        {focusAccount !== "All" && (
          <button
            className="ve-gl-filter-chip"
            onClick={() => setFocusAccount("All")}
          >
            {focusAccount}
            <span className="ve-gl-filter-chip-x">×</span>
          </button>
        )}
      </div>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <div className="ve-body" style={{ position: "relative" }}>

        {isFetching && data && <div className="ve-fetch-bar" />}

        {/* ── Top row: GL bar chart + GL Variance Pie ──────────────────────── */}
        <div className="ve-top-container">

          {/* Budget Consumed by Quarter */}
          <div className="ve-card">
            <div className="ve-card-header">
              <div>
                <div className="ve-card-title">{overrunByQuarter.title}</div>
                <div className="ve-card-subtitle">Top over-budget accounts — variance ($K) per quarter</div>
              </div>
              <div className="ve-chart-select-wrap" ref={quarterRef}>
                <div
                  className={`sdd-control ve-chart-select-control${quarterOpen ? " sdd-control--open" : ""}`}
                  onClick={() => setQuarterOpen(o => !o)}
                >
                  <span className="sdd-value">{quarterFilter}</span>
                  <svg className={`sdd-chevron${quarterOpen ? " sdd-chevron--up" : ""}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4L6 8L10 4" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {quarterOpen && (
                  <ul className="sdd-menu ve-chart-select-menu">
                    {QUARTER_OPTIONS.map(q => (
                      <li
                        key={q}
                        className={`sdd-option${quarterFilter === q ? " sdd-option--active" : ""}`}
                        onClick={() => { setQuarterFilter(q); setQuarterOpen(false); }}
                      >
                        {q}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart
                data={(quarterFilter === "All" ? overrunByQuarter.data : overrunByQuarter.data.filter((d: any) => d.quarter === quarterFilter)).map((row: any) => ({
                  ...row,
                  _total: overrunByQuarter.accounts.reduce((sum: number, acc: string) => sum + (Number(row[acc]) || 0), 0),
                }))}
                margin={{ top: 20, right: 16, left: 8, bottom: 4 }}
                barCategoryGap="20%"
                barGap={2}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#DFE2E7" strokeOpacity={0.35} vertical={false} />
                <XAxis
                  dataKey="quarter"
                  tick={{ fontSize: 10, fill: "#475569" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 9.5, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1_000_000
                      ? `$${(v / 1_000_000).toFixed(1)}M`
                      : `$${(v / 1000).toFixed(0)}K`
                  }
                  width={52}
                />
                <Tooltip
                  formatter={(v: any, name: any) => [
                    Math.abs(Number(v)) >= 1_000_000
                      ? `$${(Number(v) / 1_000_000).toFixed(2)}M`
                      : `$${(Number(v) / 1000).toFixed(0)}K`,
                    name,
                  ]}
                  contentStyle={{ fontSize: 11, color: "#64748B" }}
                  labelStyle={{ color: "#64748B" }}
                  itemStyle={{ color: "#64748B" }}
                  cursor={{ fill: "#f8fafc" }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 9, paddingTop: 4 }}
                  content={() => (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", justifyContent: "center", paddingTop: 4 }}>
                      {overrunByQuarter.accounts.map((acc: string, i: number) => (
                        <span key={acc} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: "#64748B" }}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: indexColor(i), display: "inline-block" }} />
                          {acc}
                        </span>
                      ))}
                    </div>
                  )}
                />
                {overrunByQuarter.accounts.map((acc: string, i: number) => (
                  <Bar
                    key={acc}
                    dataKey={acc}
                    stackId="a"
                    fill={indexColor(i)}
                    fillOpacity={0.7}
                    radius={i === overrunByQuarter.accounts.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                    style={{ cursor: "pointer" }}
                    onClick={() => { setFocusAccount(acc); onGlAccountSelect?.(acc); setActiveTab("quarter"); }}
                  >
                    {i === overrunByQuarter.accounts.length - 1 && (
                      <LabelList
                        dataKey="_total"
                        position="top"
                        style={{ fontSize: 9, fill: "rgb(55, 65, 81)" }}
                        formatter={(v: any) => Number(v) > 0 ? (Math.abs(Number(v)) >= 1_000_000 ? `$${(Number(v) / 1_000_000).toFixed(1)}M` : `$${(Number(v) / 1000).toFixed(0)}K`) : ""}
                      />
                    )}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance by GL Account (Variance Pie) */}
          <div className="ve-card">
            <div className="ve-card-header">
              <div>
                <div className="ve-card-title">{glVariance.title}</div>
                <div className="ve-card-subtitle">Variance ($K) — Negative = Saving, Positive = Overrun</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={230}>
              <PieChart margin={{ top: 20, right: 10, bottom: 20, left: 20 }}>
                <Pie
                  data={glVariance.data.slice(0, 8).map((d: any) => ({ ...d, absVariance: Math.abs(d.variance) }))}
                  dataKey="absVariance"
                  nameKey="name"
                  cx="40%"
                  cy="50%"
                  outerRadius={82}
                  innerRadius={34}
                  paddingAngle={2}
                  label={({ value, percent, x, y, cx }: any) =>
                    percent > 0.04
                      ? <text x={x} y={y} fill="rgb(55, 65, 81)" fontSize={8} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central">
                          {Math.abs(Number(value)) >= 1_000_000 ? `$${(Number(value) / 1_000_000).toFixed(1)}M` : `$${(Number(value) / 1000).toFixed(0)}K`}
                        </text>
                      : null
                  }
                  labelLine={{ stroke: "#94a3b8", strokeWidth: 0.8 }}
                  style={{ cursor: "pointer" }}
                  onClick={(d: any) => { setFocusAccount(d.name); onGlAccountSelect?.(d.name); setActiveTab("quarter"); }}
                >
                  {glVariance.data.slice(0, 8).map((entry: any, i: number) => (
                    <Cell key={entry.name} fill={indexColor(i)} fillOpacity={0.7} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(_v: any, _name: any, props: any) => {
                    const original = props.payload.variance;
                    const fmt = Math.abs(Number(original)) >= 1_000_000
                      ? `$${(Number(original) / 1_000_000).toFixed(2)}M`
                      : `$${(Number(original) / 1000).toFixed(0)}K`;
                    return [fmt, Number(original) > 0 ? "Overrun / Underperformance" : "Saving / Over-achievement"];
                  }}
                  contentStyle={{ fontSize: 11 }}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconSize={8}
                  iconType="circle"
                  wrapperStyle={{ fontSize: 10, lineHeight: "20px", paddingLeft: 16 }}
                  formatter={(value: string) => (
                    <span style={{ color: "#475569", fontSize: 10 }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Bottom row: Financial table + Analyst Note ─────────────────────── */}
        <div className="ve-bottom-container">

          {/* Financial Detail — tabbed drill-down */}
          <div className={`ve-card ve-table-card${isFetching && data ? " ve-card--fetching" : ""}`}>
            <div className="ve-table-header-row">
              <div className="ve-card-title">{table.title}</div>
              {focusAccount !== "All" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, color: "#64748b" }}>Focused:</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#165697" }}>{focusAccount}</span>
                  <button
                    onClick={() => setFocusAccount("All")}
                    style={{ fontSize: 10, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", padding: "0 4px" }}
                  >
                    × Clear
                  </button>
                </div>
              )}
            </div>

            {/* Tab bar */}
            <div className="ve-tabs">
              <button className={`ve-tab-btn${activeTab === "quarter" ? " ve-tab-btn--active" : ""}`} onClick={() => setActiveTab("quarter")}>
                Quarter detail
              </button>
              <button className={`ve-tab-btn${activeTab === "postings" ? " ve-tab-btn--active" : ""}`} onClick={() => setActiveTab("postings")}>
                Recent postings
              </button>
              <button className={`ve-tab-btn${activeTab === "costcenter" ? " ve-tab-btn--active" : ""}`} onClick={() => setActiveTab("costcenter")}>
                Cost center drill
              </button>
            </div>

            {/* Tab: Quarter detail */}
            {activeTab === "quarter" && (
              <div className="ve-table-wrap">
                {focusAccount === "All" && (
                  <div className="ve-drill-hint" style={{ padding: "6px 12px", fontSize: 11, color: "#94a3b8" }}>
                    Click any row to focus on an account for drill-down in other tabs.
                  </div>
                )}
                <table className="ve-table">
                  <thead>
                    <tr>
                      <th>Fiscal Year</th>
                      <th>Fiscal Quarter</th>
                      <th>Account Name</th>
                      <th>Account Category</th>
                      <th className="ve-th-num">Budget ($)</th>
                      <th className="ve-th-num">Actual ($)</th>
                      <th className="ve-th-num">Variance $</th>
                      <th className="ve-th-num">Variance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = table.rows.filter((row: any) => focusAccount === "All" || row.accountName === focusAccount);
                      if (!filtered.length) {
                        return (
                          <tr>
                            <td colSpan={8} className="ve-no-data">No data for the current filters</td>
                          </tr>
                        );
                      }
                      return filtered.map((row: any, i: number) => (
                        <tr
                          key={i}
                          className={i % 2 === 0 ? "ve-tr-even" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => setFocusAccount(focusAccount === row.accountName ? "All" : row.accountName)}
                          title="Click to focus / unfocus this account"
                        >
                          <td className="ve-td-cc">{row.fiscalYear}</td>
                          <td className="ve-td-cc">Q{row.fiscalQuarter}</td>
                          <td className="ve-td-acc">{row.accountName}</td>
                          <td className="ve-td-cc">{row.accountCategory}</td>
                          <td className="ve-td-num">${row.budgetAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="ve-td-num">${row.actualAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="ve-td-num" style={{ color: row.accountCategory !=='REVENUE' && row.variance < 0 ? RED : GREEN, fontWeight: 600 }}>
                            {row.accountCategory !=='REVENUE' && row.variance < 0 ? "-" : "+"}${Math.abs(row.variance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="ve-td-num">
                            <span className={`ve-var-pill ve-var-pill--${row.accountCategory !=='REVENUE' && row.accountCategory !=='REVENUE' && row.variance < 0 ? "unfavorable" : "favorable"}`}>
                              {row.accountCategory !=='REVENUE' && row.variance < 0 ? "-" : "+"}{Math.abs(row.variancePct)}%
                            </span>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tab: Recent postings */}
            {activeTab === "postings" && (
              <div className="ve-table-wrap">
                {focusAccount === "All" ? (
                  <div className="ve-drill-hint">Click a row in the Quarter detail tab to select an account and view its recent postings.</div>
                ) : postingsLoading ? (
                  <div className="ve-drill-hint">Loading postings…</div>
                ) : !postingsData?.rows?.length ? (
                  <div className="ve-drill-hint">No postings found for <strong>{focusAccount}</strong> in FY{filters.fiscalYear}{filters.costCenter && filters.costCenter !== "All" ? ` / ${filters.costCenter}` : ""}.</div>
                ) : (
                  <table className="ve-table">
                    <thead>
                      <tr>
                        <th>Posting Date</th>
                        <th>Document</th>
                        <th>Transaction Type</th>
                        <th>Cost Center</th>
                        <th>Vendor / Customer</th>
                        <th>Reference</th>
                        <th className="ve-th-num">Amount (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postingsData.rows.map((row: any, i: number) => (
                        <tr key={i} className={i % 2 === 0 ? "ve-tr-even" : ""}>
                          <td className="ve-td-cc">{row.postingDate ? String(row.postingDate).slice(0, 10) : "—"}</td>
                          <td className="ve-td-cc">{row.docNumber ?? "—"}</td>
                          <td className="ve-td-cc">{row.transactionType ?? "—"}</td>
                          <td className="ve-td-acc">{row.costCenterName ?? row.costCenter ?? "—"}</td>
                          <td className="ve-td-acc">{row.vendorName ?? row.customerName ?? "—"}</td>
                          <td className="ve-td-acc">{row.reference ?? "—"}</td>
                          <td className="ve-td-num">{row.amountUsd ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Tab: Cost center drill */}
            {activeTab === "costcenter" && (
              <div className="ve-table-wrap">
                {focusAccount === "All" ? (
                  <div className="ve-drill-hint">Click a row in the Quarter detail tab to select an account and view cost center detail.</div>
                ) : ccLoading ? (
                  <div className="ve-drill-hint">Loading cost center data…</div>
                ) : !costCentersData?.rows?.length ? (
                  <div className="ve-drill-hint">No cost center data found for <strong>{focusAccount}</strong> in FY{filters.fiscalYear}{filters.costCenter && filters.costCenter !== "All" ? ` / ${filters.costCenter}` : ""}.</div>
                ) : (
                  <table className="ve-table">
                    <thead>
                      <tr>
                        <th>Cost Center</th>
                        <th>Cost Center Name</th>
                        <th className="ve-th-num">Actual Amount ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costCentersData.rows.map((row: any, i: number) => (
                        <tr key={i} className={i % 2 === 0 ? "ve-tr-even" : ""}>
                          <td className="ve-td-cc">{row.costCenter ?? "—"}</td>
                          <td className="ve-td-acc">{row.costCenterName ?? "—"}</td>
                          <td className="ve-td-num">${row.actualAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div className="ve-table-footer">
              <span className="ve-footer-source">Data Source: Snowflake Curated Finance Dataset (ABB.SAP)</span>
              <span className="ve-footer-refresh">LAST REFRESH: {table.lastRefresh} UTC</span>
            </div>
          </div>

          {/* Analyst Note */}
          <div className="ve-card ve-analyst-card">
            <div className="ve-analyst-eyebrow">ANALYST NOTE</div>
            <p className="ve-analyst-text">
              Significant GL driver annual gap detected across{" "}
              <strong>{filters.accountCategory ?? "All"}</strong> cost centres. Travel &amp; Services
              categories show the highest deviation vs forecast.
            </p>

            {topVariance && (
              <div className="ve-analyst-top-var">
                <div className="ve-tv-eyebrow">Top Variance – Highest Impact</div>
                <div className="ve-tv-name">{topVariance.name}</div>
                <div className="ve-tv-stats">
                  <div className="ve-tv-stat">
                    <span className="ve-tv-stat-label">Actual</span>
                    <span className="ve-tv-stat-value" style={{ color: ACTUAL_C }}>${topVariance.actual}M</span>
                  </div>
                  <div className="ve-tv-stat">
                    <span className="ve-tv-stat-label">Forecast</span>
                    <span className="ve-tv-stat-value" style={{ color: BLUE }}>${topVariance.forecast}M</span>
                  </div>
                  <div className="ve-tv-stat">
                    <span className="ve-tv-stat-label">Variance</span>
                    <span className="ve-tv-stat-value" style={{ color: topVariance.favorable ? GREEN : RED }}>
                      {topVariance.variance > 0 ? "+" : ""}${Math.abs(topVariance.variance)}K
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="ve-analyst-stat-grid">
              <div className="ve-analyst-stat-box">
                <span className="ve-analyst-stat-label">TOTAL BUDGET</span>
                <span className="ve-analyst-stat-value" style={{ color: BLUE }}>
                  ${totalBudget != null ? parseFloat(totalBudget).toFixed(1) : "—"}M
                </span>
              </div>
              <div className="ve-analyst-stat-box">
                <span className="ve-analyst-stat-label">VARIANCE</span>
                <span className="ve-analyst-stat-value" style={{ color: totalVariance != null && totalVariance <= 0 ? GREEN : RED }}>
                  {totalVariance != null ? (totalVariance >= 0 ? "+" : "") + parseFloat(totalVariance).toFixed(1) + "M" : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default VarianceExplorer;
